import React, { useContext, useState } from 'react';
import { Box, Container, Typography, Link as MuiLink, IconButton, Divider, useTheme, Snackbar, Alert } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { ThemeContext } from '../contexts/ThemeContext';

// 🆕 AI Képzési logók importálása
import LightModeMainLogo from '../assets/images/lightlogo.jpeg';
import DarkModeMainLogo from '../assets/images/darklogo.jpeg';

const Footer: React.FC = () => {
  const theme = useTheme();
  const { darkMode } = useContext(ThemeContext) || { darkMode: false };

  // 🆕 Snackbar állapot a vágólapra másolás visszajelzéséhez
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const emailAddress = "csillagtanoda.info@gmail.com";

  const handleCopyEmail = (e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText(emailAddress);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  // 🆕 Dinamikus logó kiválasztása a témának megfelelően
  const currentMainLogo = darkMode ? LightModeMainLogo : DarkModeMainLogo; 

  // Modern translucent background with blur
  const bgColor = darkMode ? 'rgba(18,10,57,0.88)' : 'rgba(243,230,246,0.92)';
  const textColor = darkMode ? theme.palette.grey[200] : theme.palette.grey[800];
  const accentColor = theme.palette.primary.main;

  return (
    <Box component="footer" sx={{ position: 'relative', zIndex: 2, width: '100%' }}>
      <Box
        sx={{
          backdropFilter: 'blur(20px)',
          backgroundColor: bgColor,
          borderTop: '4px solid #FCC632',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: 6,
              color: textColor,
            }}
          >
            {/* Logo & Info */}
            <Box sx={{ textAlign: { xs: 'center', md: 'left' }, flex: 1 }}>
              <MuiLink component={RouterLink} to="/" underline="none" sx={{ display: 'inline-flex', alignItems: 'center', mb: 1 }}>
                <Box
                  component="img"
                  src={currentMainLogo} // ⬅️ Itt cseréltük a logót
                  alt="Logo"
                  sx={{ height: 56 }}
                />
                <Typography variant="h5" sx={{ ml: 1, fontWeight: 800, color: accentColor }}>
                  CSILLAGTANODA
                </Typography>
              </MuiLink>
              <Typography variant="subtitle2" sx={{ fontStyle: 'italic', mb: 2 }}>
                Lásd magadban a csodát!
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <IconButton component="a" href="https://www.facebook.com/csillagtanoda" aria-label="Facebook" sx={{ color: accentColor }}>
                  <FacebookIcon />
                </IconButton>
                <IconButton component="a" href="https://www.instagram.com/csillagtanoda" aria-label="Instagram" sx={{ color: accentColor }}>
                  <InstagramIcon />
                </IconButton>
                {/* 🆕 E-mail gomb a bal oldali sávban is vágólapra másol */}
                <IconButton onClick={handleCopyEmail} aria-label="Email" sx={{ color: accentColor }}>
                  <EmailIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Links & Contact */}
            <Box sx={{ flex: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' } }}>
                <Typography variant="h6" sx={{ mb: 1, color: accentColor }}>
                  Gyors Linkek
                </Typography>
                {['/', '/maganora', '/gyermekagykontroll', '/tanulasitrening', '/chillag', '/ai'].map((to) => (
                  <MuiLink
                    key={to}
                    component={RouterLink}
                    to={to}
                    sx={{ my: 0.5, fontSize: '0.95rem', color: textColor, '&:hover': { color: accentColor } }}
                  >
                    {to.replace('/', '') || 'Főoldal'}
                  </MuiLink>
                ))}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' } }}>
                <Typography variant="h6" sx={{ mb: 1, color: accentColor }}>
                  Kapcsolat
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
                  <PhoneIcon fontSize="small" sx={{ color: accentColor }} />
                  <Typography variant="body2">+36 30 447 4333</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" sx={{ color: accentColor }} />
                  {/* 🆕 E-mail címre kattintás is vágólapra másol és megállítja az alapértelmezett linket */}
                  <MuiLink href={`mailto:${emailAddress}`} onClick={handleCopyEmail} sx={{ color: textColor, cursor: 'pointer' }}>
                    {emailAddress}
                  </MuiLink>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Divider & bottom */}
          <Divider sx={{ my: 4, borderColor: theme.palette.divider }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '0.8rem',
              color: textColor,
            }}
          >
            <Typography>&copy; {new Date().getFullYear()} Csillagtanoda. Minden jog fenntartva.</Typography>
            <Box sx={{ mt: { xs: 1, md: 0 }, display: 'flex', gap: 2 }}>
              <MuiLink component={RouterLink} to="/privacy-policy" sx={{ '&:hover': { color: accentColor } }}>
                Adatvédelmi Nyilatkozat
              </MuiLink>
              <MuiLink component={RouterLink} to="/terms-of-service" sx={{ '&:hover': { color: accentColor } }}>
                ÁSZF
              </MuiLink>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 🆕 Visszajelző Snackbar értesítés */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" variant="filled" sx={{ width: '100%' }}>
          E-mail cím a vágólapra másolva: {emailAddress}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Footer;